import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./CardModal.module.css";
import classNames from "classnames";
import CardImage from "../../assets/Credit_Card_Icon.svg";
import CalendarIcon from "../../assets/calendar_Icon.svg";
import CardInfoBox from "../CardInfoBox";
import SafeDropdown from "../SafeDropdown";
import DateSelect from "../DateSelect";
import Modal from "../Modal";
import CollectExample from "../../CollectExample";
import Loader from "components/Loader";
import { useTranslation } from "react-i18next";

export function CardModal({
  isOpen,
  onClose,
  card,
  onAdd,
  isEditing,
  onValidate,
  onTokenReceived,
  isLoading,
}) {
  const submitButtonRef = useRef(null);
  const addPaymentButtonRef = useRef(null);
  const { t } = useTranslation();

  const initialCardInfo = {
    zipCode: "",
    nickName: "",
    lastFour: "",
    expMonth: "",
    expYear: "",
    cardType: "",
    cardHolder: "",
    isPrimary: false,
    membershipCreditCardId: null,
  };

  const [cardInfo, setCardInfo] = useState(initialCardInfo);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isEditCardNumber, setIsEditCardNumber] = useState(false);
  const [triggerCollectClick, setTriggerCollectClick] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [validationStatus, setValidationStatus] = useState({
    zipCode: null,
    nickName: null,
    lastFour: isEditing ? true : null,
    expMonth: null,
    expYear: null,
  });

  const [errors, setErrors] = useState({
    zipCode: "",
    nickName: "",
    lastFour: "",
    expMonth: "",
    expYear: "",
  });

  const [fieldFocus, setFieldFocus] = useState({
    zipCode: false,
    nickName: false,
    lastFour: false,
    expMonth: false,
    expYear: false,
  });

  const initializeCardInfo = (isEditing, card) => {
    if (isEditing && card) {
      return {
        zipCode: card.zipCode,
        nickName: card.nickName,
        lastFour: card.lastFour,
        expMonth: card.expMonth,
        expYear: card.expYear,
        cardHolder: card.cardHolder,
        cardType: card.cartType,
        isPrimary: card.isPrimary,
        membershipCreditCardId: card.membershipCreditCardId,
      };
    }
    return initialCardInfo;
  };

  const setInitialValidationState = (isEditing) => {
    const initialValidationState = {
      zipCode: isEditing ? true : null,
      nickName: isEditing ? true : null,
      lastFour: isEditing ? true : null,
      expMonth: isEditing ? true : null,
      expYear: isEditing ? true : null,
    };
    setValidationStatus(initialValidationState);
    setErrors(
      Object.keys(initialValidationState).reduce(
        (acc, key) => ({ ...acc, [key]: "" }),
        {}
      )
    );
  };

  useEffect(() => {
    if (isSubmitting) {
      return;
    }

    if (isOpen) {
      // if (isEditing && card && card !== previousCard.current) {
      if (isEditing && card) {
        const newCardInfo = initializeCardInfo(isEditing, card);
        setCardInfo(newCardInfo);
        setInitialValidationState(true);
        // previousCard.current = card;
      } else if (!isEditing) {
        setCardInfo(initialCardInfo);
        setInitialValidationState(false);
      }
    }
  }, [isOpen, isEditing, card, isSubmitting]);

  const handleFocus = (field) => {
    setFieldFocus((prev) => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field, value) => {
    if (fieldFocus[field]) {
      validateInput(field, value);
    }
  };

  //Handles input change
  const handleChange = (field, value) => {
    let formattedValue = value;

    if (field === "lastFour" && !isEditing) {
      formattedValue =
        "lastFour" && !isEditing ? formatCardNumber(value) : value;
    }

    if (field === "zipCode") {
      formattedValue = value.slice(0, 7);
    }
    setCardInfo((prev) => ({ ...prev, [field]: formattedValue }));

    validateInput(field, formattedValue);
  };

  //Validate input
  const validateInput = (field, value) => {
    let isValid = true;
    let error = "";

    switch (field) {
      case "zipCode":
        isValid = /^(\d{5}(-\d{4})?|[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d)$/.test(
          value
        );
        error = isValid ? "" : t("Enter a valid Zip/Postal");
        break;
      case "nickName":
        isValid = value.trim().length > 0;
        error = isValid ? "" : t("Enter a nick name for this card");
        break;
      default:
        isValid = true;
    }

    setValidationStatus((prev) => ({ ...prev, [field]: isValid }));
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const displayCardNumber = () =>
    isEditing && !isEditCardNumber
      ? `************${cardInfo.lastFour}`
      : cardInfo.lastFour;

  const formatCardNumber = (value) =>
    value
      .replace(/\D/g, "")
      .slice(0, 16)
      .replace(/(\d{4})(?=\d)/g, "$1 ");

  const validateAllInputs = () => {
    Object.keys(cardInfo).forEach((field) => {
      validateInput(field, cardInfo[field]);
    });
  };

  //Handles form submit
  const handleSubmit = async (event) => {
    event.preventDefault();

    // setIsProcessing(true);
    setIsSubmitting(true);

    try {
      if (isEditing) {
        await onValidate(cardInfo);
        handleOnClose();
      } else {
        setValidationStatus((prev) => ({
          ...prev,
          zipCode: true,
          nickName: true,
        }));
        setTriggerCollectClick(true);
        await onAdd(cardInfo);
      }
    } catch (error) {
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const [collectValidation, setCollectValidation] = useState({
    ccnumberValid: null,
    ccexpValid: null,
  });

  const handleCollectValidation = (validation) => {
    setCollectValidation({
      ccnumberValid: validation.ccnumberValid,
      ccexpValid: validation.ccexpValid,
    });
  };

  const hasErrors = () => {
    if (!isEditing) {
      return (
        errors.zipCode !== "" ||
        validationStatus.zipCode === null ||
        errors.nickName !== "" ||
        validationStatus.nickName === null ||
        collectValidation.ccnumberValid === false ||
        collectValidation.ccexpValid === false
      );
    } else {
      return (
        Object.values(validationStatus).some(
          (status) => status === false || status === null
        ) ||
        Object.values(errors).some((error) => error !== "") ||
        collectValidation.ccnumberValid === false ||
        collectValidation.ccexpValid === false
      );
    }
  };

  const handleValidation = (event) => {
    event.preventDefault();

    const hasValidationErrors = hasErrors();

    if (hasValidationErrors) {
      // Update fieldFocus to true for all fields with errors
      setFieldFocus((prev) => {
        const newFieldFocus = { ...prev };
        Object.keys(validationStatus).forEach((field) => {
          if (!validationStatus[field] || errors[field]) {
            newFieldFocus[field] = true;
          }
        });

        // Add focus for Collect.js fields if invalid
        if (!fieldFocus.ccnumber && !collectValidation.ccnumberValid) {
          newFieldFocus["ccnumber"] = true;
        }
        if (!fieldFocus.ccexp && !collectValidation.ccexpValid) {
          newFieldFocus["ccexp"] = true;
        }

        return newFieldFocus;
      });

      // Revalidate non-Collect.js fields
      validateAllInputs();
    } else {
      // Trigger the invisible submit button if validation passes
      submitButtonRef.current.click();
      addPaymentButtonRef.current.click();
    }
  };

  //Styles for input focus
  const inputFieldStyle = (fieldName) => {
    return classNames({
      [styles.inputFocus]: fieldFocus[fieldName],
      [styles.inputFocusError]:
        fieldFocus[fieldName] && validationStatus[fieldName] === false,
      [styles.inputFocusValid]: validationStatus[fieldName] === true,
    });
  };

  const handleOnClose = () => {
    if (isLoading) {
      onClose();
      return;
    }

    setCardInfo(initialCardInfo);
    setTriggerCollectClick(false);
    setIsDropdownOpen(false);
    setCollectValidation({
      ccnumberValid: null,
      ccexpValid: null,
    });
    setValidationStatus({
      zipCode: null,
      nickName: null,
      lastFour: null,
      expMonth: null,
      expYear: null,
    });
    setErrors({
      zipCode: "",
      nickName: "",
      lastFour: "",
      expMonth: "",
      expYear: "",
    });
    setIsSubmitting(false);
    onClose();
  };

  return (
    <>
      <Modal isOpen={isOpen} className={styles.modal} onClose={handleOnClose}>
        {isLoading ? (
          <div className={styles.loaderContainer}>
            <Loader color={"#415364"} size={85} />
          </div>
        ) : (
          <form className={styles.container}>
            <>
              <div className={styles.header}>
                <h3 className={styles.title}>{t("Payment Information")}</h3>
                <div className={styles.acceptedCards}>
                  {t("We currently accept")}
                </div>
              </div>

              <div className={styles.cardContainer}>
                <div className={styles.input}>
                  <CardInfoBox
                    className={inputFieldStyle("zipCode")}
                    value={cardInfo.zipCode || ""}
                    label={t("Zip Code")}
                    placeholder={t("Enter Billing Zip Code")}
                    onFocus={() => handleFocus("zipCode")}
                    onBlur={(e) => handleBlur("zipCode", e.target.value)}
                    onChange={(e) => handleChange("zipCode", e.target.value)}
                    isValid={validationStatus.zipCode}
                  />
                </div>
                <div className={styles.input}>
                  <CardInfoBox
                    className={inputFieldStyle("nickName")}
                    value={cardInfo.nickName || ""}
                    label={t("Nickname")}
                    placeholder={t("Enter Card Nickname")}
                    onFocus={() => handleFocus("nickName")}
                    onBlur={(e) => handleBlur("nickName", e.target.value)}
                    onChange={(e) => handleChange("nickName", e.target.value)}
                    isValid={validationStatus.nickName}
                  />
                </div>
                {!isEditing ? (
                  <CollectExample
                    triggerClick={triggerCollectClick}
                    onTokenReceived={onTokenReceived}
                    onValidationChange={handleCollectValidation}
                    updateCollectValidation={setCollectValidation}
                    forceError={{
                      ccnumber:
                        fieldFocus.ccnumber && !collectValidation.ccnumberValid,
                      ccexp: fieldFocus.ccexp && !collectValidation.ccexpValid,
                    }}
                  />
                ) : (
                  <>
                    <div className={styles.input}>
                      <CardInfoBox
                        className={inputFieldStyle("lastFour")}
                        value={displayCardNumber()}
                        img={CardImage}
                        placeholder={t("Enter Card Number")}
                        onFocus={() => setIsEditCardNumber(true)}
                        onBlur={() => setIsEditCardNumber(false)}
                        onChange={(e) =>
                          handleChange("lastFour", e.target.value)
                        }
                        isValid={validationStatus.lastFour}
                        disabled={isEditing}
                      />
                    </div>
                    <div
                      className={styles.input}
                      // onClick={handleExpirationClick}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <DateSelect
                        className={`${inputFieldStyle(
                          "expMonth"
                        )} ${inputFieldStyle("expYear")}`}
                        img={CalendarIcon}
                        expDate={`${cardInfo.expMonth || "MM"}/${
                          cardInfo.expYear || "YY"
                        }`}
                        onDateChange={({ month, year }) => {
                          handleChange("expMonth", month);
                          handleChange("expYear", year);
                        }}
                        isValid={
                          validationStatus.expMonth && validationStatus.expYear
                        }
                        disabled={isEditing}
                      />
                    </div>
                  </>
                )}
              </div>
              <div className={styles.errorContainer}>
                {Object.keys(errors).map((fieldName) => {
                  if (fieldFocus[fieldName] && errors[fieldName]) {
                    return (
                      <div key={fieldName} className={styles.errorMsg}>
                        {errors[fieldName]}
                      </div>
                    );
                  }
                  return null;
                })}

                {fieldFocus.ccnumber && !collectValidation.ccnumberValid && (
                  <div className={styles.errorMsg}>
                    {t("Enter a Valid Card Number")}
                  </div>
                )}
                {fieldFocus.ccexp && !collectValidation.ccexpValid && (
                  <div className={styles.errorMsg}>
                    {t("Enter a Valid Card Expiration")}
                  </div>
                )}
              </div>
              <div className={styles.dropdown}>
                <SafeDropdown
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                />
              </div>
              <div className={styles.buttons}>
                <button
                  type="button"
                  onClick={handleOnClose}
                  className={styles.closeBtn}
                >
                  {t("Cancel/Close")}
                </button>
                {/* Invisible button to trigger form submission */}
                <button
                  type="submit"
                  ref={submitButtonRef}
                  onClick={handleSubmit}
                  style={{ display: "none" }}
                ></button>

                {/* Visible button to trigger validation */}
                <button
                  type="button"
                  onClick={handleValidation}
                  ref={addPaymentButtonRef}
                  className={styles.addBtn}
                  disabled={isSubmitting}
                >
                  {isEditing ? t("Update Payment") : t("Add Payment")}
                </button>
              </div>
            </>
          </form>
        )}
      </Modal>
    </>
  );
}

CardModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  card: PropTypes.object,
  onAdd: PropTypes.func,
  onValidate: PropTypes.func,
  isEditing: PropTypes.bool,
  onTokenReceived: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default CardModal;
